import React, { useCallback, useMemo } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { CharacterCount, CharacterCountProps } from './character-count'
import {
  hasRestrictedValue,
  isValidAnonymousTextLimit,
  isValidMinCharacterLimit,
  isValidTextLimit,
  TextLimitConfig,
  getMaxCharacterLimit,
} from './validations'
import { WordCount } from './word-count'
import { FixedHorizontalSpacer } from '@grammarly/badger-ui'
import { InputValidationType } from '../../lib/types/component-text-input'

export enum InputError {
  InputRequired = 'Please fill out this field.',
  SelectRequired = 'Please select an item in the list.',
  InvalidEmail = 'Please enter a valid email.',
  DefaultRestrictedValue = 'This is a restricted value.',
  BelowCharacterLimit = 'Please add more text.',
  ExceededCharacterLimit = 'Please shorten your text.',
  ExceededAnonymousLimit = 'Log in to use longer text.',
}

export const useError = (name: string, textLimitConfig?: TextLimitConfig): React.ReactNode | string | undefined => {
  const { errors } = useFormState({ name })
  const value = useWatch({ name })

  const errorMessage: string | React.ReactNode | undefined = errors[name]?.message?.toString()

  // Priority anonymous character limit and then regular character limit (assume anonymous is less)
  if (typeof value === 'string') {
    const { maxCharacterLimit, minCharacterLimit, anonymousCharacterLimit, useWordLimit, showErrorText } =
      textLimitConfig ?? {}

    const error = showErrorText && errorMessage
    let maxChars: number | undefined = undefined
    let LengthCounter: ((props: CharacterCountProps) => React.JSX.Element) | undefined = undefined

    if (errorMessage === InputError.BelowCharacterLimit && minCharacterLimit) {
      LengthCounter = useWordLimit ? WordCount : CharacterCount
      maxChars = anonymousCharacterLimit ?? maxCharacterLimit
    }

    if (errorMessage === InputError.ExceededAnonymousLimit && anonymousCharacterLimit) {
      LengthCounter = useWordLimit ? WordCount : CharacterCount
      maxChars = anonymousCharacterLimit
    } else if (errorMessage === InputError.ExceededCharacterLimit && maxCharacterLimit) {
      if (useWordLimit) {
        // Check for adversarial inputs first with no spaces.
        const twiceMaxCharacterLimit = maxCharacterLimit * 2

        LengthCounter = value.length <= twiceMaxCharacterLimit ? WordCount : CharacterCount
        maxChars = value.length <= twiceMaxCharacterLimit ? maxCharacterLimit : twiceMaxCharacterLimit
      } else {
        LengthCounter = CharacterCount
        maxChars = maxCharacterLimit
      }
    }

    if (LengthCounter && maxChars) {
      return (
        <>
          {error}
          <FixedHorizontalSpacer multiplier={1} />
          <LengthCounter value={value} maxChars={maxChars} />
        </>
      )
    }
  }

  return errorMessage
}

export function useTextLimitConfig({
  maxLength,
  hasLongformText,
  minCharacterLimit,
  anonymousCharacterLimit,
  useWordLimit,
}: {
  maxLength?: number
  hasLongformText?: boolean
  minCharacterLimit?: number
  anonymousCharacterLimit?: number
  useWordLimit?: boolean
}): TextLimitConfig {
  return useMemo(
    () => ({
      maxCharacterLimit: getMaxCharacterLimit(maxLength, hasLongformText),
      showErrorText: true,
      minCharacterLimit,
      anonymousCharacterLimit,
      useWordLimit,
    }),
    [anonymousCharacterLimit, hasLongformText, maxLength, minCharacterLimit, useWordLimit]
  )
}

export function useValidation({
  validationType,
  neverBounceValidation = true,
  restrictedValues,
  restrictedValuesErrorMessage,
  isAnonymous,
  isConditionalMet = true,
  textLimitConfig,
  validations,
}: {
  validationType?: InputValidationType
  neverBounceValidation?: boolean
  restrictedValues?: string[]
  restrictedValuesErrorMessage?: string
  isAnonymous: boolean
  isConditionalMet?: boolean
  textLimitConfig: TextLimitConfig
  validations: Record<InputValidationType, (val: string) => boolean | string>
}) {
  return useCallback(
    (val: string) => {
      if (isConditionalMet) {
        if (textLimitConfig.minCharacterLimit && !isValidMinCharacterLimit(val, textLimitConfig)) {
          return InputError.BelowCharacterLimit
        }
        if (
          textLimitConfig.anonymousCharacterLimit &&
          isAnonymous &&
          !isValidAnonymousTextLimit(val, textLimitConfig)
        ) {
          return InputError.ExceededAnonymousLimit
        }
        if (textLimitConfig.maxCharacterLimit && !isValidTextLimit(val, textLimitConfig)) {
          return InputError.ExceededCharacterLimit
        }
        if (restrictedValues && hasRestrictedValue(val, restrictedValues)) {
          return restrictedValuesErrorMessage ? restrictedValuesErrorMessage : InputError.DefaultRestrictedValue
        }
        if (!neverBounceValidation) {
          return InputError.InvalidEmail
        }
        if (validationType) {
          return validations[validationType](val)
        }
        return true
      } else {
        return true
      }
    },
    [
      isConditionalMet,
      textLimitConfig,
      isAnonymous,
      restrictedValues,
      neverBounceValidation,
      validationType,
      restrictedValuesErrorMessage,
      validations,
    ]
  )
}
